import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { shape, string } from "prop-types";
import { useForm } from "react-hook-form";
import { z } from "zod";

import Button from "~components/Button";
import TextInput from "~components/forms/TextInput";
import { apiFetch } from "~lib/apiFetch";
import RichText from "~modules/RichText";
import Notification from "~ts_components/Notification/Notification";

const AccountLoginPageTemplate = ({
  accountLoginPage: { title, image, button },
}) => {
  const { t } = useTranslation();

  const schema = z.object({
    email: z
      .string({ required_error: t("loginPage.inputMailRequired") })
      .trim()
      .email(t("loginPage.inputMailInvalid")),
  });

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onTouched",
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  const onSubmit = async ({ email }) => {
    try {
      const response = await apiFetch("/account/login", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        throw new Error("Failed to login");
      }
    } catch (err) {
      setError("root", {
        type: "server",
        message: t("loginPage.formError"),
      });
    }
  };

  return (
    <div className="bg-grey-light flex flex-col gap-10 max-md:px-4 py-8 md:py-16 -mt-4">
      <h1 className="text-header-1 text-center">{title}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 max-w-240 mx-auto min-h-160 rounded-lg overflow-hidden">
        {/* Form section */}
        <div className="bg-brand-secondary-contrast order-2 w-full flex flex-col gap-16 p-8 lg:order-1">
          {!isSubmitSuccessful && !errors.root?.message ? (
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              className="flex flex-col gap-8"
            >
              <fieldset className="flex flex-col gap-6" disabled={isSubmitting}>
                <legend className="sr-only"> {t("loginPage.formTitle")}</legend>
                <h2 className="text-body-large-bold">
                  {t("loginPage.inputMailTitle")}
                </h2>

                <TextInput
                  id="email"
                  type="email"
                  label={t("loginPage.inputMailLabel")}
                  placeholder={t("loginPage.inputMailPlaceholder")}
                  className="!border-grey-border !border"
                  hasError={Boolean(errors.email)}
                  errorMessage={errors.email?.message}
                  {...register("email")}
                />

                <p className="text-body-small">
                  {t("loginPage.inputMailDescription")}
                </p>
              </fieldset>

              <div className="flex flex-col gap-2">
                <Button
                  type="submit"
                  color="secondary"
                  disabled={!isValid || isSubmitting}
                >
                  {isSubmitting ? "Bezig…" : "Inloggen"}
                </Button>
                {button?.label && (
                  <Button
                    href={button.internalLink?.[0]?.slug ?? button.externalLink}
                    variant="outline"
                    type="button"
                  >
                    {button.label}
                  </Button>
                )}
              </div>

              {/* server-side error */}
              {errors.root?.message && (
                <Notification variant="error" aria-live="assertive">
                  {errors.root.message}
                </Notification>
              )}
            </form>
          ) : (
            <div className="w-full flex flex-col gap-4">
              <h2 className="text-header-3">{t("loginPage.successTitle")}</h2>
              <RichText
                html={t("loginPage.successDescription", {
                  email: getValues("email"),
                })}
              />
            </div>
          )}
        </div>

        {/* Image section */}
        <div className="relative max-sm:hidden order-1 lg:order-2 min-h-60">
          <Image src={image.url} alt="" fill className="object-cover" />
        </div>
      </div>
    </div>
  );
};

AccountLoginPageTemplate.propTypes = {
  accountLoginPage: shape({
    title: string.isRequired,
    image: shape({
      url: string.isRequired,
    }).isRequired,
    button: shape({
      label: string,
      internalLink: shape({
        slug: string,
      }).isRequired,
    }),
  }).isRequired,
};

export default AccountLoginPageTemplate;
